import React, { useCallback } from 'react'
import { HeartIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import PageMasthead from '../../components/PageMasthead'
import { formatDistanceToNow } from 'date-fns'
import { useGetAllServicesHealthQuery } from '../../api/client'
import Spinner from '../../components/Spinner'

interface ServiceHealthItem {
    serviceName: string
    lastHeartbeat: string
    expectedIntervalSeconds: number
    healthStatus: 'HEALTHY' | 'WARNING' | 'CRITICAL'
    createdAt: string
    updatedAt?: string
}

const getHealthStatusColor = (status: string) => {
    switch (status) {
        case 'HEALTHY':
            return 'text-green-700 bg-green-50 ring-1 ring-inset ring-green-600/20'
        case 'WARNING':
            return 'text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-600/20'
        case 'CRITICAL':
            return 'text-red-700 bg-red-50 ring-1 ring-inset ring-red-600/20'
        default:
            return 'text-ink-600 bg-ink-100 ring-1 ring-inset ring-ink-300/40'
    }
}

const getHealthStatusIcon = (status: string) => {
    switch (status) {
        case 'HEALTHY':
            return '●'
        case 'WARNING':
            return '◐'
        case 'CRITICAL':
            return '●'
        default:
            return '○'
    }
}

const ServiceHealthPage: React.FC = () => {
    const { data, loading, error, refetch } = useGetAllServicesHealthQuery({
        pollInterval: 30000, // Auto-refresh every 30 seconds
        errorPolicy: 'all'
    })

    const handleRefresh = useCallback(() => {
        refetch()
    }, [refetch])

    const services: ServiceHealthItem[] = data?.getAllServicesHealth?.items || []
    const totalServices = services.length
    const healthyCount = services.filter(
        (s: ServiceHealthItem) => s.healthStatus === 'HEALTHY'
    ).length
    const warningCount = services.filter(
        (s: ServiceHealthItem) => s.healthStatus === 'WARNING'
    ).length
    const criticalCount = services.filter(
        (s: ServiceHealthItem) => s.healthStatus === 'CRITICAL'
    ).length

    const columns = [
        {
            key: 'serviceName',
            label: 'Service name',
            render: (service: ServiceHealthItem) => (
                <div className="font-medium text-ink-800">{service.serviceName}</div>
            )
        },
        {
            key: 'healthStatus',
            label: 'Status',
            render: (service: ServiceHealthItem) => {
                const getTooltipText = (status: string) => {
                    switch (status) {
                        case 'HEALTHY':
                            return 'Within 2x expected interval'
                        case 'WARNING':
                            return 'Within 5x expected interval'
                        case 'CRITICAL':
                            return 'Beyond 5x expected interval'
                        default:
                            return ''
                    }
                }

                return (
                    <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getHealthStatusColor(
                            service.healthStatus
                        )}`}
                        title={getTooltipText(service.healthStatus)}
                    >
                        <span className="mr-1">{getHealthStatusIcon(service.healthStatus)}</span>
                        {service.healthStatus}
                    </span>
                )
            }
        },
        {
            key: 'lastHeartbeat',
            label: 'Last heartbeat',
            render: (service: ServiceHealthItem) => (
                <div className="text-sm text-ink-800">
                    {formatDistanceToNow(new Date(service.lastHeartbeat), { addSuffix: true })}
                    <div className="text-xs text-ink-500">
                        {new Date(service.lastHeartbeat).toLocaleString()}
                    </div>
                </div>
            )
        },
        {
            key: 'expectedIntervalSeconds',
            label: (
                <div className="flex items-center space-x-1">
                    <span>Expected interval</span>
                    <QuestionMarkCircleIcon
                        className="h-4 w-4 text-ink-400 hover:text-ink-600 cursor-help"
                        title="How often the service is expected to send heartbeat signals. Used to determine health status."
                    />
                </div>
            ),
            render: (service: ServiceHealthItem) => (
                <div className="text-sm text-ink-800">
                    {service.expectedIntervalSeconds}s
                    <div className="text-xs text-ink-500">
                        ({Math.round(service.expectedIntervalSeconds / 60)} min)
                    </div>
                </div>
            )
        }
    ]

    if (error) {
        return (
            <div className="min-h-screen bg-ink-50 p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">
                                    Error loading service health data
                                </h3>
                                <div className="mt-2 text-sm text-red-700">{String(error)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-ink-50">
            <PageMasthead
                title="Service health monitor"
                tabTitle="Service health monitor"
                subtitle={`${totalServices} service${
                    totalServices !== 1 ? 's' : ''
                } • ${healthyCount} healthy, ${warningCount} warning, ${criticalCount} critical`}
                actions={
                    <div className="flex items-center space-x-4">
                        <div className="text-sm text-ink-500">Auto-refreshes every 30 seconds</div>
                        <button
                            onClick={handleRefresh}
                            className="inline-flex items-center px-3 py-2 border border-ink-200 shadow-sm text-xs font-medium rounded-md text-ink-700 bg-white hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arwen-500"
                        >
                            Refresh
                        </button>
                    </div>
                }
            />

            <div className="flex flex-col px-6 py-4">
                <p className="mb-4 font-sans text-sm text-ink-500">
                    This page shows the heartbeats of our services. No recent heartbeats means the
                    service is probably not functioning.
                </p>
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Spinner size={6} />
                    </div>
                ) : services.length === 0 ? (
                    <div className="text-center py-12">
                        <HeartIcon className="mx-auto h-12 w-12 text-ink-400" />
                        <div className="mt-2 text-sm text-ink-500">No services found</div>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
                        <table className="min-w-full divide-y divide-ink-100">
                            <thead className="bg-ink-50">
                                <tr>
                                    {columns.map((column) => (
                                        <th
                                            key={column.key}
                                            className="px-6 py-3 text-left text-[10.5px] font-semibold text-ink-500 uppercase tracking-wide"
                                        >
                                            {column.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-ink-100">
                                {services.map((service, index) => (
                                    <tr key={index} className="hover:bg-ink-50">
                                        {columns.map((column) => (
                                            <td
                                                key={column.key}
                                                className="px-6 py-4 whitespace-nowrap"
                                            >
                                                {column.render(service)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}

export default ServiceHealthPage
